import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Youtube, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

export function ytId(url = "") {
  const m = url.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
}

export default function YouTubePage() {
  const { content } = useSite();
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    api.get("/youtube").then(({ data }) => setVideos(data)).catch(() => {});
  }, []);

  const yt = content?.youtube || {};

  return (
    <div data-testid="youtube-page" className="pt-32 pb-16 px-5 max-w-7xl mx-auto">
      <div className="text-center mb-16">
        <div className="w-14 h-14 mx-auto rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-6">
          <Youtube className="text-red-400" size={26} />
        </div>
        <p className="text-xs tracking-[0.28em] uppercase font-mono-accent text-amber-400/70 mb-4">Chaîne YouTube</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-amber-50 mb-5">{yt.title || "Enseignements en vidéo"}</h1>
        <p className="text-slate-400 text-lg max-w-2xl mx-auto">{yt.subtitle}</p>
        {yt.channel_url && (
          <a
            href={yt.channel_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="youtube-channel-link"
            className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-red-500/90 hover:bg-red-500 text-white font-medium transition-colors"
          >
            <Youtube size={18} /> Voir la chaîne <ExternalLink size={15} />
          </a>
        )}
      </div>

      {videos.length === 0 ? (
        <p className="text-center text-slate-500">Les vidéos seront bientôt disponibles.</p>
      ) : (
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {videos.map((v, i) => {
            const id = ytId(v.url);
            return (
              <motion.div
                key={v.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="glass rounded-2xl overflow-hidden"
                data-testid={`youtube-card-${i}`}
              >
                <div className="aspect-video bg-black">
                  {id ? (
                    <iframe
                      className="w-full h-full"
                      src={`https://www.youtube.com/embed/${id}`}
                      title={v.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <a href={v.url} target="_blank" rel="noopener noreferrer" className="w-full h-full flex items-center justify-center text-amber-300">
                      Ouvrir la vidéo
                    </a>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="font-serif text-xl text-amber-100 mb-2">{v.title}</h3>
                  {v.tags && <span className="text-xs font-mono-accent text-amber-400/70">{v.tags}</span>}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
